import { FC, useEffect, useMemo, useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { useFieldArray, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AddIcon } from "../../../../../assets/icons";
import FormSectionTitle from "../../../../../components/forms/FormSectionTitle";
import { CarbonSnapFormData } from "../../../../../datasources/carbonsnap/models";
import { createEmptyTransportNode, syncArcsWithNodes } from "../../../../../datasources/carbonsnap/utils";
import ArcControls from "./ArcControls";
import RouteNodeInput from "./RouteNodeInput";

interface Props {
	form: UseFormReturn<CarbonSnapFormData>;
	isLoading?: boolean;
}

const TripComposer: FC<Props> = ({ form, isLoading }) => {
	const { t } = useTranslation("feature.carbonsnap");
	const { fields, append, remove, move } = useFieldArray({
		control: form.control,
		name: "nodes",
	});
	const nodes = useWatch({ control: form.control, name: "nodes" });
	const arcs = useWatch({ control: form.control, name: "arcs" });
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
	const [overIndex, setOverIndex] = useState<number | null>(null);

	const fieldIds = useMemo(() => fields.map((field) => field.id), [fields]);

	// Derive nextArcs from nodes using useMemo (not useEffect)
	const nextArcs = useMemo(() => {
		return syncArcsWithNodes(nodes ?? [], arcs ?? []);
	}, [nodes, arcs]);

	// Sync form value only when nextArcs differs from current arcs
	useEffect(() => {
		const currentArcs = arcs ?? [];
		if (nextArcs.length !== currentArcs.length) {
			form.setValue("arcs", nextArcs);
			return;
		}
		const changed = nextArcs.some((arc, index) => {
			const current = currentArcs[index];
			return !current || current.fromNodeId !== arc.fromNodeId || current.toNodeId !== arc.toNodeId;
		});
		if (changed) {
			form.setValue("arcs", nextArcs);
		}
	}, [nextArcs, arcs, form]);

	const handleDragStart = (index: number) => (event: React.DragEvent<HTMLButtonElement>) => {
		setDraggedIndex(index);
		setOverIndex(index);
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/plain", `${index}`);
	};

	const handleDragOver = (index: number) => (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		if (overIndex !== index) {
			setOverIndex(index);
		}
	};

	const handleDrop = (index: number) => (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		const fromIndex = Number(event.dataTransfer.getData("text/plain"));
		if (!Number.isNaN(fromIndex) && fromIndex !== index) {
			move(fromIndex, index);
		}
		setDraggedIndex(null);
		setOverIndex(null);
	};

	const handleDragEnd = () => {
		setDraggedIndex(null);
		setOverIndex(null);
	};

	return (
		<Stack spacing={1} width="100%">
			<FormSectionTitle title={t("route/composer/title")} infoTooltip={t("route/composer/info toolltip")} />

			{fields.map((field, index) => (
				<Box key={field.id}>
					<Box
						onDragOver={handleDragOver(index)}
						onDrop={handleDrop(index)}
						sx={{
							borderRadius: 1,
							border:
								overIndex === index && draggedIndex !== null ? "1px dashed" : "1px solid transparent",
							borderColor: overIndex === index && draggedIndex !== null ? "primary.main" : "transparent",
							px: 0.5,
							opacity: draggedIndex === index ? 0.7 : 1,
						}}
					>
						<RouteNodeInput
							form={form}
							nodeIndex={index}
							isFirst={index === 0}
							isLast={index === fields.length - 1}
							isLoading={isLoading}
							canDelete={fields.length > 2}
							onDelete={() => remove(index)}
							dragHandleProps={{
								draggable: fieldIds.length > 1,
								onDragStart: handleDragStart(index),
								onDragEnd: handleDragEnd,
							}}
						/>
					</Box>
					{index < fields.length - 1 && (
						<ArcControls form={form} arcIndex={index} fromNodeIndex={index} toNodeIndex={index + 1} />
					)}
				</Box>
			))}

			<Button
				variant="outlined"
				startIcon={<AddIcon />}
				fullWidth
				onClick={() => append(createEmptyTransportNode())}
			>
				{t("route/composer/add node")}
			</Button>
		</Stack>
	);
};

export default TripComposer;
